/**
 * Utilitário: carrega TIFF RGB via geotiff.js e desenha em canvas.
 */
import { fromArrayBuffer } from 'geotiff';

/**
 * @param {ArrayBuffer} arrayBuffer
 * @returns {Promise<{ width: number, height: number, rgba: Uint8ClampedArray }>}
 */
export async function decodeTiffToRgba(arrayBuffer) {
  const tiff = await fromArrayBuffer(arrayBuffer);
  const image = await tiff.getImage();
  const width = image.getWidth();
  const height = image.getHeight();
  const rasters = await image.readRasters({ interleave: false });

  const bandCount = rasters.length;
  const rgba = new Uint8ClampedArray(width * height * 4);

  const sample = (band, i) => {
    const v = band[i];
    if (v == null || Number.isNaN(v)) return 0;
    // 8-bit típico; se >255, escala simples
    if (v <= 255) return Math.max(0, Math.min(255, Math.round(v)));
    return Math.max(0, Math.min(255, Math.round((v / 65535) * 255)));
  };

  for (let i = 0; i < width * height; i++) {
    const o = i * 4;
    if (bandCount >= 3) {
      rgba[o] = sample(rasters[0], i);
      rgba[o + 1] = sample(rasters[1], i);
      rgba[o + 2] = sample(rasters[2], i);
    } else {
      const g = sample(rasters[0], i);
      rgba[o] = g;
      rgba[o + 1] = g;
      rgba[o + 2] = g;
    }
    rgba[o + 3] = 255;
  }

  return { width, height, rgba };
}

/**
 * Desenha ImageData em um canvas (redimensionado para maxWidth se necessário).
 */
export function paintRgbaToCanvas(canvas, width, height, rgba, maxDisplayWidth = 1200) {
  const scale = width > maxDisplayWidth ? maxDisplayWidth / width : 1;
  const dw = Math.round(width * scale);
  const dh = Math.round(height * scale);

  const off = document.createElement('canvas');
  off.width = width;
  off.height = height;
  const octx = off.getContext('2d');
  octx.putImageData(new ImageData(rgba, width, height), 0, 0);

  canvas.width = dw;
  canvas.height = dh;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.drawImage(off, 0, 0, dw, dh);

  return { displayWidth: dw, displayHeight: dh, scale, imageWidth: width, imageHeight: height };
}

/**
 * Constrói FeatureCollection a partir de polígonos em coords normalizadas 0–1.
 * @param {Array<Array<[number, number]>>} polygons - lista de anéis externos [[x,y],...]
 */
export function polygonsToFeatureCollection(polygons) {
  const features = (polygons || [])
    .filter((ring) => ring && ring.length >= 3)
    .map((ring, idx) => {
      const closed = [...ring];
      const [fx, fy] = closed[0];
      const [lx, ly] = closed[closed.length - 1];
      if (fx !== lx || fy !== ly) closed.push([fx, fy]);
      return {
        type: 'Feature',
        properties: { index: idx, label: `supressao_${idx + 1}` },
        geometry: {
          type: 'Polygon',
          coordinates: [closed],
        },
      };
    });
  return { type: 'FeatureCollection', features };
}

export function featureCollectionToPolygons(fc) {
  if (!fc) return [];
  const features = Array.isArray(fc)
    ? fc
    : Array.isArray(fc.features)
      ? fc.features
      : [];
  return features
    .map((f) => {
      const coords = f?.geometry?.coordinates?.[0];
      if (!Array.isArray(coords)) return null;
      // remove ponto de fechamento se existir
      const ring = coords.map(([x, y]) => [Number(x), Number(y)]);
      if (
        ring.length > 1 &&
        ring[0][0] === ring[ring.length - 1][0] &&
        ring[0][1] === ring[ring.length - 1][1]
      ) {
        ring.pop();
      }
      return ring;
    })
    .filter(Boolean);
}
